import React from 'react';
import {
  Box,
  Button,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import type { IAccount } from '../../../interfaces/Account/IAccount.ts';
import {
  overviewItemTitleStyles,
  overviewItemValueStyles,
  overviewSectionContainerStyles,
  overviewSectionTitleStyles,
} from '../../../styles/ts/overviewStyles.ts';
import AleaPhoneIcon from '../../../icons/AleaPhoneIcon.tsx';
import AleaEmailIcon from '../../../icons/AleaEmailIcon.tsx';

type CockpitUsersSectionProps = {
  isArchived: boolean;
  canEdit: boolean;
  cockpitUsers: IAccount[];
  isCockpitUsersLoading: boolean;
  activeActionRowId: string | null;
  onSetActiveActionRowId: (id: string | null) => void;
  currentUserId?: string;
  onOpenInvite: () => void;
  onOpenAccountSettings: () => void;
  onResendInvite: (accountId: string) => void;
};

const CockpitUsersSection: React.FC<CockpitUsersSectionProps> = ({
  isArchived,
  canEdit,
  cockpitUsers,
  isCockpitUsersLoading,
  activeActionRowId,
  onSetActiveActionRowId,
  currentUserId,
  onOpenInvite,
  onOpenAccountSettings,
  onResendInvite,
}) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{ ...overviewSectionContainerStyles, pb: '50px', mb: '50px' }}
      w="100%"
    >
      <Box opacity={isArchived ? 0.5 : 1}>
        {/*section title*/}
        <Flex
          alignItems={{ base: 'start', md: 'center' }}
          direction={{ base: 'column', md: 'row' }}
          mb="20px"
          justifyContent={{ base: 'flex-start', md: 'space-between' }}
        >
          <Box>
            <Text sx={overviewSectionTitleStyles} mb={{ base: 1, md: 1 }}>
              {t('userWithCockpitAccessHeadline')}
            </Text>
            <Text
              fontSize="15px"
              color="alea.500"
              mb={{ base: '10px', md: 0 }}
            >
              {t('userWithCockpitAccessSubline')}
            </Text>
          </Box>
          {canEdit && (
            <Button
              data-testid="cockpit-add-user-button"
              leftIcon={<AddIcon />}
              colorScheme="teal"
              variant="solid"
              h="50px"
              borderRadius="20px"
              fontWeight={400}
              fontSize="20px"
              onClick={onOpenInvite}
              minW="240px"
            >
              {t('addUser')}
            </Button>
          )}
        </Flex>

        {/* simple table */}
        <Box
          borderRadius="20px"
          overflowX="auto"
          overflowY="hidden"
          border="1px solid #EAEAEA"
        >
          {/* header */}
          <Flex
            bg="#F3F2F2"
            px="16px"
            py="14px"
            align="center"
            borderBottom="1px solid #EAEAEA"
            minW="1015px"
            position="relative"
          >
            <Box w="120px" flexShrink={0}>
              <Text sx={overviewItemTitleStyles}>{t('title')}</Text>
            </Box>
            <Box w="130px" flexShrink={0}>
              <Text sx={overviewItemTitleStyles}>{t('firstName')}</Text>
            </Box>
            <Box w="200px" flexShrink={0}>
              <Text sx={overviewItemTitleStyles}>{t('lastName')}</Text>
            </Box>
            <Box w="240px" flexShrink={0}>
              <Text sx={overviewItemTitleStyles}>{t('role')}</Text>
            </Box>
            <Box flex="1" minW="300px" flexShrink={0}>
              <Text sx={overviewItemTitleStyles}>{t('contact')}</Text>
            </Box>
            <Box
              w="45px"
              flexShrink={0}
              position="sticky"
              right="0"
              bg="#F3F2F2"
              zIndex={1}
              borderLeft="1px solid #EAEAEA"
              ml="-1px"
            />
          </Flex>

          {/* rows */}
          {cockpitUsers.length > 0 ? (
            cockpitUsers.map((account) => {
              const isActive = activeActionRowId === account.id;
              const isInvited = !account.last_login;
              const isInviteExpired =
                isInvited &&
                !!account.updated_at &&
                Date.now() - new Date(account.updated_at).getTime() >
                  3 * 24 * 60 * 60 * 1000;

              return (
                <Flex
                  key={account.id}
                  bg={isActive ? '#F8F8F8' : 'white'}
                  align="center"
                  h="82px"
                  borderBottom="1px solid #EAEAEA"
                  _last={{ borderBottom: 'none' }}
                  minW="1015px"
                  position={isActive ? 'relative' : 'static'}
                  zIndex={isActive ? 1501 : 'auto'}
                  boxShadow={
                    isActive ? '0px 8px 24px rgba(0,0,0,0.12)' : 'none'
                  }
                >
                  {isInvited ? (
                    <>
                      {isInviteExpired ? (
                        <>
                          <Box
                            w="690px"
                            px="16px"
                            flexShrink={0}
                            display="flex"
                            justifyContent="center"
                          >
                            <Text
                              fontSize="15px"
                              fontWeight={400}
                              color="#DA2A2A"
                              textAlign="center"
                              fontStyle="italic"
                            >
                              {t('invitationLinkExpired')}{' '}
                              <Box
                                as="span"
                                textDecoration="underline"
                                cursor="pointer"
                                fontStyle="italic"
                                onClick={() => onResendInvite(account.id)}
                              >
                                {t('resendLink')}
                              </Box>
                            </Text>
                          </Box>
                          <Box
                            flex="1"
                            minW="300px"
                            px="16px"
                            flexShrink={0}
                          >
                            <Flex align="center">
                              <Box mr="8px">
                                <AleaEmailIcon size="20" />
                              </Box>
                              <Text
                                sx={{
                                  ...overviewItemValueStyles,
                                  color: '#DA2A2A',
                                }}
                                textDecoration="underline"
                                fontStyle="italic"
                                noOfLines={1}
                              >
                                {account.email || '-'}
                              </Text>
                            </Flex>
                          </Box>
                        </>
                      ) : (
                        <>
                          <Box
                            w="690px"
                            px="16px"
                            flexShrink={0}
                            display="flex"
                            justifyContent="center"
                          >
                            <Text
                              fontSize="15px"
                              fontWeight={300}
                              color="#C6C6C6"
                              fontStyle="italic"
                              textAlign="center"
                            >
                              {t('invitationEmailHasBeenSent')}
                            </Text>
                          </Box>
                          <Box
                            flex="1"
                            minW="300px"
                            px="16px"
                            flexShrink={0}
                          >
                            <Flex align="center">
                              <Box mr="8px">
                                <AleaEmailIcon size="20" />
                              </Box>
                              <Text
                                sx={{
                                  ...overviewItemValueStyles,
                                  color: '#C6C6C6',
                                }}
                                textDecoration="underline"
                                fontStyle="italic"
                                noOfLines={1}
                              >
                                {account.email || '-'}
                              </Text>
                            </Flex>
                          </Box>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <Box w="120px" px="16px" flexShrink={0}>
                        <Text sx={overviewItemValueStyles}>
                          {account.title ? t(account.title) : '-'}
                        </Text>
                      </Box>
                      <Box w="130px" px="16px" flexShrink={0}>
                        <Text sx={overviewItemValueStyles}>
                          {account.first_name || '-'}
                        </Text>
                      </Box>
                      <Box w="200px" px="16px" flexShrink={0}>
                        <Text sx={overviewItemValueStyles}>
                          {account.last_name || '-'}
                        </Text>
                      </Box>
                      <Box w="240px" px="16px" flexShrink={0}>
                        <Text sx={overviewItemValueStyles}>
                          {account.role || '-'}
                        </Text>
                      </Box>
                      <Box flex="1" minW="300px" px="16px" flexShrink={0}>
                        <Flex direction="column" gap="6px">
                          <Flex align="center">
                            <Box mr="8px">
                              <AleaPhoneIcon size="20" />
                            </Box>
                            <Text
                              sx={overviewItemValueStyles}
                              textDecoration={
                                (account?.compound_mobile_phone_number?.code &&
                                  account?.compound_mobile_phone_number
                                    ?.phone) ||
                                (account?.compound_phone_number?.code &&
                                  account?.compound_phone_number?.phone)
                                  ? 'underline'
                                  : 'none'
                              }
                            >
                              {account?.compound_mobile_phone_number?.code &&
                              account?.compound_mobile_phone_number?.phone
                                ? `${account.compound_mobile_phone_number.code} ${account.compound_mobile_phone_number.phone}`
                                : account?.compound_phone_number?.code &&
                                    account?.compound_phone_number?.phone
                                  ? `${account.compound_phone_number.code} ${account.compound_phone_number.phone}`
                                  : '-'}
                            </Text>
                          </Flex>
                          <Flex align="center">
                            <Box mr="8px">
                              <AleaEmailIcon size="20" />
                            </Box>
                            <Text
                              sx={overviewItemValueStyles}
                              textDecoration="underline"
                              noOfLines={1}
                            >
                              {account.email || '-'}
                            </Text>
                          </Flex>
                        </Flex>
                      </Box>
                    </>
                  )}

                  {account.id === currentUserId ? (
                    <Menu
                      isOpen={activeActionRowId === account.id}
                      onOpen={() => onSetActiveActionRowId(account.id)}
                      onClose={() => onSetActiveActionRowId(null)}
                      placement="bottom-end"
                      autoSelect={false}
                    >
                      <MenuButton
                        as={Box}
                        w="25px"
                        h="82px"
                        mt="1px"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        bg={isActive ? 'alea.500' : '#F3F2F2'}
                        borderLeft="1px solid #EAEAEA"
                        cursor="pointer"
                        flexShrink={0}
                        position="sticky"
                        right="0"
                        zIndex={isActive ? 1502 : 2}
                      >
                        <IconButton
                          aria-label="more"
                          variant="ghost"
                          color={isActive ? 'white' : '#6E6E6E'}
                          size="xs"
                          w="25px"
                          h="82px"
                          cursor="pointer"
                          _hover={{ background: 'transparent' }}
                          icon={
                            <Box as="span" fontSize="20px">
                              ⋮
                            </Box>
                          }
                        />
                      </MenuButton>
                      <Portal>
                        <MenuList
                          borderRadius="20px"
                          border="1px solid #DCDCDC"
                          boxShadow="0px 8px 24px rgba(0,0,0,0.15)"
                          p="0"
                          zIndex={1502}
                          overflow="hidden"
                          bg="white"
                          motionProps={{
                            variants: {
                              enter: { opacity: 1, scale: 1 },
                              exit: {
                                opacity: 0,
                                scale: 1,
                              },
                            },
                          }}
                        >
                          <MenuItem
                            onClick={() => {
                              onSetActiveActionRowId(null);
                              onOpenAccountSettings();
                            }}
                            fontSize="18px"
                            h="52px"
                            borderBottom="1px solid #DCDCDC"
                          >
                            {t('edit')}
                          </MenuItem>
                        </MenuList>
                      </Portal>
                    </Menu>
                  ) : (
                    <Box
                      w="25px"
                      h="82px"
                      mt="1px"
                      bg="transparent"
                      borderLeft="none"
                      flexShrink={0}
                    />
                  )}
                </Flex>
              );
            })
          ) : (
            <Flex bg="white" px="16px" py="12px" align="center">
              <Text sx={overviewItemValueStyles}>
                {isCockpitUsersLoading
                  ? t('general.loading', { defaultValue: 'Loading...' })
                  : '-'}
              </Text>
            </Flex>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CockpitUsersSection;

